import { supabase } from '../../utils/supabase';
import { LeaderboardPlayer, GameHistoryItem, DebugPlayerRecord } from './types';
import { PenaltyRecord, GameRecord } from '../../types/stats';
import { calculateWinRate, calculateAvgScore } from './calculator';
import { statsCache, getTierForDate, isBeyond30Days } from '../../utils/statsCache';

/**
 * V106: 数据一致性守护配置
 */
const CONSISTENCY_CHECK_CONFIG = {
  // 差异阈值：超过此值触发自动修复
  AUTO_FIX_THRESHOLD: 0,
  // 是否启用自动修复
  AUTO_FIX_ENABLED: true,
  // 缓存有效期（毫秒）
  CACHE_TTL: 5 * 60 * 1000, // 5分钟
};


export class StatsDataService {
  // V106: 数据一致性守护服务

  /**
   * V111: 数据一致性守护 - 检查榜单数据与原始记录是否一致（按昵称统计）
   * @returns {Promise<{isConsistent: boolean; differences: number}>} 一致性检查结果
   */
  async checkDataConsistency(dojoFilter: string = 'all', seasonFilter: string = 'all'): Promise<{
    isConsistent: boolean;
    differences: number;
    playerDiffs: Array<{ playerName: string; gameDiff: number; scoreDiff: number }>;
  }> {
    try {
      console.log('[V111] 执行数据一致性守护检查...');

      // 获取原始数据统计
      const records = await this.fetchRawPlayerRecords(dojoFilter, seasonFilter);
      const rawStats = this.aggregatePlayerStats(records);

      // 获取缓存数据
      const { data: cachedStats } = await supabase.from('player_stats').select('*');
      const cachedMap: Record<string, any> = {};
      for (const stat of cachedStats || []) {
        cachedMap[stat.player_name] = stat;
      }

      // 对比差异
      const playerDiffs: Array<{ playerName: string; gameDiff: number; scoreDiff: number }> = [];
      const allPlayers = new Set([
        ...Object.keys(rawStats),
        ...Object.keys(cachedMap),
      ]);

      for (const playerName of allPlayers) {
        const raw = rawStats[playerName];
        const cached = cachedMap[playerName];

        const rawGames = raw?.total_games || 0;
        const rawScore = raw?.total_score || 0;
        const cachedGames = cached?.total_games || 0;
        const cachedScore = cached?.total_score || 0;

        const gameDiff = Math.abs(cachedGames - rawGames);
        const scoreDiff = Math.abs((cachedScore || 0) - (rawScore || 0));

        if (gameDiff > 0 || scoreDiff > 0.01) {
          playerDiffs.push({ playerName, gameDiff, scoreDiff });
        }
      }

      const isConsistent = playerDiffs.length === 0;
      console.log(`[V111] 一致性检查完成: ${isConsistent ? '✅ 数据一致' : `❌ 发现 ${playerDiffs.length} 处差异`}`);

      return { isConsistent, differences: playerDiffs.length, playerDiffs };
    } catch (err) {
      console.error('[V111] 一致性检查失败:', err);
      return { isConsistent: false, differences: -1, playerDiffs: [] };
    }
  }

  /**
   * V111: 自动修复数据不一致
   */
  async autoFixIfNeeded(dojoFilter: string = 'all', seasonFilter: string = 'all'): Promise<boolean> {
    if (!CONSISTENCY_CHECK_CONFIG.AUTO_FIX_ENABLED) return false;

    const { isConsistent, differences } = await this.checkDataConsistency(dojoFilter, seasonFilter);

    if (!isConsistent && differences > CONSISTENCY_CHECK_CONFIG.AUTO_FIX_THRESHOLD) {
      console.log(`[V111] 检测到 ${differences} 处数据差异，自动触发重算...`);
      await this.recalculateGlobalStats();
      
      // 重算后再次检查
      const recheck = await this.checkDataConsistency(dojoFilter, seasonFilter);
      console.log(`[V111] 自动修复完成: ${recheck.isConsistent ? '✅ 已修复' : `❌ 仍有 ${recheck.differences} 处差异`}`);
      return recheck.isConsistent;
    }

    return isConsistent;
  }

  /**
   * V607: 初始化 player_stats 缓存表数据
   * 注意：日常数据更新由数据库触发器自动处理，此方法仅用于首次初始化或数据修复
   */
  async recalculateGlobalStats(): Promise<void> {
    console.warn('[V607] recalculateGlobalStats 已废弃 - 数据更新由触发器自动处理');
    console.log('[V607] 如需初始化数据，请在 Supabase Dashboard 运行：SELECT fn_update_player_stats_from_round(player_nickname) FROM player_round_records;');
    
    // V619: 清除所有玩家统计缓存，强制重新加载
    statsCache.remove('player_stats:all:all');
    for (let dojoId = 1; dojoId <= 10; dojoId++) {
      for (let seasonId = 1; seasonId <= 10; seasonId++) {
        statsCache.remove(`player_stats:${dojoId}:${seasonId}`);
      }
    }
    console.log('[V619] 已清除所有玩家统计缓存');
  }

  /**
   * V527: 根据日期范围智能选择数据源
   * - 近 30 日：查询原始表 player_round_records（准实时）
   * - 30 日外：查询物化视图 mv_player_stats_daily（高性能，T+1 更新）
   */
  private async fetchWithSmartDataSource(
    startDate: string,
    endDate: string,
    dojoFilter?: number,
    seasonFilter?: number
  ): Promise<{
    useMaterializedView: boolean;
    data: any[];
  }> {
    const startBeyond30 = isBeyond30Days(startDate);
    const endBeyond30 = isBeyond30Days(endDate);
    
    // 如果整个日期范围都在 30 日外，使用物化视图
    if (startBeyond30 && endBeyond30) {
      console.log(`[V527] 日期范围 [${startDate} ~ ${endDate}] 超过 30 天，使用物化视图`);
      
      let query = supabase
        .from('mv_player_stats_daily')
        .select('*')
        .gte('stats_date', startDate)
        .lte('stats_date', endDate);
      
      if (dojoFilter !== undefined) {
        query = query.eq('dojo_id', dojoFilter);
      }
      if (seasonFilter !== undefined) {
        query = query.eq('season_id', seasonFilter);
      }
      
      const { data, error } = await query;
      
      if (error) {
        console.error('[V527] 查询物化视图失败:', error);
        throw error;
      }
      
      return { useMaterializedView: true, data: data || [] };
    }
    
    // 否则使用原始表（近 30 日或跨范围）
    console.log(`[V527] 日期范围 [${startDate} ~ ${endDate}] 在 30 天内或跨范围，使用原始表`);
    
    let query = supabase
      .from('player_round_records')
      .select('*, game_records!inner(*)')
      .eq('is_deleted', 'n')
      .eq('status', 'valid')
      .eq('game_records.is_deleted', 'n')
      .eq('game_records.status', 'valid');
    
    if (dojoFilter !== undefined) {
      query = query.eq('game_records.dojo_id', dojoFilter);
    }
    if (seasonFilter !== undefined) {
      query = query.eq('game_records.season_id', seasonFilter);
    }
    if (startDate && endDate) {
      query = query.gte('game_records.game_date', startDate)
        .lte('game_records.game_date', endDate);
    }
    
    const { data: records, error } = await query;
    
    if (error) {
      console.error('[V527] 查询原始表失败:', error);
      throw error;
    }
    
    return { useMaterializedView: false, data: records || [] };
  }

  /**
   * V106: 统一的原始数据查询方法
   * 所有统计逻辑都从此方法获取原始数据，确保数据源一致
   */
  private async fetchRawPlayerRecords(dojoFilter: string, seasonFilter: string): Promise<any[]> {
    let query = supabase
      .from('player_round_records')
      .select('*, game_records!inner(*)')
      .eq('is_deleted', 'n')
      .eq('status', 'valid')
      .eq('game_records.is_deleted', 'n')
      .eq('game_records.status', 'valid');

    if (dojoFilter !== 'all') {
      query = query.eq('game_records.dojo_id', Number(dojoFilter));
    }
    if (seasonFilter !== 'all') {
      query = query.eq('game_records.season_id', Number(seasonFilter));
    }

    const { data: records, error } = await query;

    if (error) {
      console.error('[V109] 查询原始数据失败:', error);
      throw error;
    }

    return records || [];
  }

  /**
   * V147: 修复数据一致性 - 统一的去重和聚合逻辑（按玩家昵称统计）
   * 关键修复：确保总场次 = 所有角色出场次数之和
   */
  private aggregatePlayerStats(records: any[]): Record<string, Partial<LeaderboardPlayer>> {
    console.log('[V147] aggregatePlayerStats 开始处理，原始记录数:', records.length);

    // V147: 添加详细的数据检查
    const rawDataSample = records.slice(0, 3).map(r => ({
      id: r.id,
      player_number: r.player_number,
      player_nickname: r.player_nickname,
      game_record_id: r.game_record_id,
      hasGameRecords: !!r.game_records,
    }));
    console.log('[V147] 原始数据样本:', rawDataSample);

    // V147: 严格去重 - 按 player_name + game_record_id
    const uniqueRecordsMap = new Map<string, any>();
    const duplicateLog: Array<{ playerName: string; gameId: number; count: number }> = [];
    const gameIdCountMap = new Map<string, number>();

    for (const record of records) {
      // V147: 使用 player_nickname 作为唯一标识
      const playerName = record.player_nickname?.trim() || `${record.player_number}号`;
      
      if (!record.game_record_id) {
        console.warn('[V147] 跳过无效记录:', { id: record.id, player_name: playerName, game_record_id: record.game_record_id });
        continue;
      }

      const key = `${playerName}_${record.game_record_id}`;
      gameIdCountMap.set(key, (gameIdCountMap.get(key) || 0) + 1);
      
      // 只保留第一条记录（严格去重）
      if (!uniqueRecordsMap.has(key)) {
        uniqueRecordsMap.set(key, { ...record, _player_name: playerName });
      }
    }

    // V147: 检测重复记录
    for (const [key, count] of gameIdCountMap.entries()) {
      if (count > 1) {
        const [playerName, gameId] = key.split('_');
        duplicateLog.push({ playerName, gameId: Number(gameId), count });
      }
    }

    if (duplicateLog.length > 0) {
      console.warn('[V147] 发现重复记录:', duplicateLog);
    }

    console.log(`[V147] 去重后 ${uniqueRecordsMap.size} 条有效记录`);

    // V147: 按 player_name 聚合统计
    const statsMap: Record<string, Partial<LeaderboardPlayer>> = {};

    for (const record of uniqueRecordsMap.values()) {
      const playerName = record._player_name;
      
      // V147: 正确处理 game_records 关联数据
      let game = record.game_records;
      
      if (!game) {
        console.warn(`[V147] 玩家${playerName}的记录缺少game_records数据:`, { recordId: record.id, gameId: record.game_record_id });
        continue;
      }

      // V147: 初始化玩家统计数据
      if (!statsMap[playerName]) {
        statsMap[playerName] = {
          player_number: record.player_number || 0,
          player_name: playerName,
          total_score: 0,
          total_games: 0,
          wins: 0,
          losses: 0,
          good_wins: 0,
          good_losses: 0,
          evil_wins: 0,
          evil_losses: 0,
          mvp_count: 0,
          percival_wins: 0,
          percival_games: 0,
          merlin_wins: 0,
          merlin_games: 0,
          lancelot_good_wins: 0,
          lancelot_good_games: 0,
          lancelot_evil_wins: 0,
          lancelot_evil_games: 0,
          loyal_wins: 0,
          loyal_games: 0,
          mordred_wins: 0,
          mordred_games: 0,
          morgana_wins: 0,
          morgana_games: 0,
          assassin_wins: 0,
          assassin_games: 0,
          assassin_merlin_kills: 0,
          minion_wins: 0,
          minion_games: 0,
          oberon_wins: 0,
          oberon_games: 0,
          win_rate: 0,
          avg_score: 0,
        };
      }

      const scoreChange = Number(record.score_change) || 0;
      const isWin = scoreChange > 0;
      const stats = statsMap[playerName]!;

      // V147: 累加基础统计
      stats.total_score = (stats.total_score || 0) + scoreChange;
      stats.total_games = (stats.total_games || 0) + 1;
      stats.wins = (stats.wins || 0) + (isWin ? 1 : 0);
      stats.losses = (stats.losses || 0) + (isWin ? 0 : 1);
      stats.good_wins = (stats.good_wins || 0) + (record.faction === 'good' && isWin ? 1 : 0);
      stats.good_losses = (stats.good_losses || 0) + (record.faction === 'good' && !isWin ? 1 : 0);
      stats.evil_wins = (stats.evil_wins || 0) + (record.faction === 'evil' && isWin ? 1 : 0);
      stats.evil_losses = (stats.evil_losses || 0) + (record.faction === 'evil' && !isWin ? 1 : 0);
      stats.mvp_count = (stats.mvp_count || 0) + (record.is_mvp ? 1 : 0);

      // V147: 统计角色数据
      const role = record.player_role as string;
      if (role === 'percival') {
        stats.percival_games = (stats.percival_games || 0) + 1;
        if (isWin && record.faction === 'good') stats.percival_wins = (stats.percival_wins || 0) + 1;
      }
      if (role === 'merlin') {
        stats.merlin_games = (stats.merlin_games || 0) + 1;
        if (isWin && record.faction === 'good') stats.merlin_wins = (stats.merlin_wins || 0) + 1;
      }
      if (role === 'lancelot_good') {
        stats.lancelot_good_games = (stats.lancelot_good_games || 0) + 1;
        if (isWin && record.faction === 'good') stats.lancelot_good_wins = (stats.lancelot_good_wins || 0) + 1;
      }
      if (role === 'lancelot_evil') {
        stats.lancelot_evil_games = (stats.lancelot_evil_games || 0) + 1;
        if (isWin && record.faction === 'evil') stats.lancelot_evil_wins = (stats.lancelot_evil_wins || 0) + 1;
      }
      if (role === 'loyal') {
        stats.loyal_games = (stats.loyal_games || 0) + 1;
        if (isWin && record.faction === 'good') stats.loyal_wins = (stats.loyal_wins || 0) + 1;
      }
      if (role === 'mordred') {
        stats.mordred_games = (stats.mordred_games || 0) + 1;
        if (isWin && record.faction === 'evil') stats.mordred_wins = (stats.mordred_wins || 0) + 1;
      }
      if (role === 'morgana') {
        stats.morgana_games = (stats.morgana_games || 0) + 1;
        if (isWin && record.faction === 'evil') stats.morgana_wins = (stats.morgana_wins || 0) + 1;
      }
      if (role === 'assassin') {
        stats.assassin_games = (stats.assassin_games || 0) + 1;
        const isMerlinKill = isWin && game?.victory_type === 'assassin_success';
        if (isMerlinKill) {
          stats.assassin_wins = (stats.assassin_wins || 0) + 1;
          stats.assassin_merlin_kills = (stats.assassin_merlin_kills || 0) + 1;
        }
      }
      if (role === 'minion') {
        stats.minion_games = (stats.minion_games || 0) + 1;
        if (isWin && record.faction === 'evil') stats.minion_wins = (stats.minion_wins || 0) + 1;
      }
      if (role === 'oberon') {
        stats.oberon_games = (stats.oberon_games || 0) + 1;
        if (isWin && record.faction === 'evil') stats.oberon_wins = (stats.oberon_wins || 0) + 1;
      }
    }

    // V147: 数据一致性校验 - 确保总场次 = 角色明细之和
    for (const [playerName, stats] of Object.entries(statsMap)) {
      const roleGamesSum = 
        (stats.percival_games || 0) +
        (stats.merlin_games || 0) +
        (stats.lancelot_good_games || 0) +
        (stats.lancelot_evil_games || 0) +
        (stats.loyal_games || 0) +
        (stats.mordred_games || 0) +
        (stats.morgana_games || 0) +
        (stats.assassin_games || 0) +
        (stats.minion_games || 0) +
        (stats.oberon_games || 0);
      
      const totalGames = stats.total_games || 0;
      
      if (roleGamesSum !== totalGames) {
        console.error(`[V147] ⚠️ 数据不一致: ${playerName} 总场次(${totalGames}) ≠ 角色明细之和(${roleGamesSum})`);
        // V147: 自动修复 - 以总场次为准（因为总场次来自去重后的记录数）
        // 这里不修改数据，而是记录警告供后续分析
      }
    }

    console.log(`[V147] 聚合完成，共 ${Object.keys(statsMap).length} 位唯一玩家`);
    return statsMap;
  }

  /**
   * V147: 修复数据一致性 - 实时加载玩家统计
   * 关键修复：确保总场次 = 所有角色出场次数之和 = 去重后的记录数
   */
  async loadPlayerStats(
    dojoFilter: string, 
    seasonFilter: string,
    forceRefresh: boolean = false
  ): Promise<{
    playerStats: LeaderboardPlayer[];
    totalGames: number;
    factionWins: { good: number; evil: number };
  }> {
    // V664-FIX: 缓存 key 在整个函数作用域内有效
    const cacheKey = `player_stats:${dojoFilter}:${seasonFilter}`;
    
    try {
      console.log('[V664] 开始加载玩家统计...', { dojoFilter, seasonFilter, forceRefresh });
      
      // V664-FIX: 强制刷新时跳过本地缓存检查
      console.log('[V664] 开始加载玩家统计...', { dojoFilter, seasonFilter, forceRefresh });

      // V664-FIX: 强制刷新时跳过本地缓存检查
      if (!forceRefresh) {
        // V525: 检查本地缓存
        const cacheKey = `player_stats:${dojoFilter}:${seasonFilter}`;
        const cached = await statsCache.get<{
          playerStats: LeaderboardPlayer[];
          totalGames: number;
          factionWins: { good: number; evil: number };
        }>(cacheKey);
        if (cached) {
          console.log('[V525] 使用缓存数据');
          return cached;
        }
        console.log('[V525] 缓存未命中，从数据库加载');
      } else {
        console.log('[V664] 强制刷新模式：跳过本地缓存，从底表实时计算');
      }

      // V664-FIX: 强制刷新时直接从底表实时计算，不依赖 player_stats 缓存表和触发器
      if (forceRefresh) {
        console.log('[V664] 从 game_records 底表实时计算统计数据...');
        
        // 从原始记录实时聚合
        const records = await this.fetchRawPlayerRecords(dojoFilter, seasonFilter);
        const statsMap = this.aggregatePlayerStats(records);

        const playerStats: LeaderboardPlayer[] = Object.values(statsMap).map(stats => {
          const winRate = calculateWinRate(stats.wins || 0, stats.total_games || 0);
          const avgScore = calculateAvgScore(stats.total_score || 0, stats.total_games || 0);
          return {
            player_number: stats.player_number || 0,
            player_name: stats.player_name || '',
            total_score: stats.total_score || 0,
            total_games: stats.total_games || 0,
            wins: stats.wins || 0,
            losses: stats.losses || 0,
            good_wins: stats.good_wins || 0,
            good_losses: stats.good_losses || 0,
            evil_wins: stats.evil_wins || 0,
            evil_losses: stats.evil_losses || 0,
            mvp_count: stats.mvp_count || 0,
            percival_wins: stats.percival_wins || 0,
            percival_games: stats.percival_games || 0,
            merlin_wins: stats.merlin_wins || 0,
            merlin_games: stats.merlin_games || 0,
            lancelot_good_wins: stats.lancelot_good_wins || 0,
            lancelot_good_games: stats.lancelot_good_games || 0,
            lancelot_evil_wins: stats.lancelot_evil_wins || 0,
            lancelot_evil_games: stats.lancelot_evil_games || 0,
            loyal_wins: stats.loyal_wins || 0,
            loyal_games: stats.loyal_games || 0,
            mordred_wins: stats.mordred_wins || 0,
            mordred_games: stats.mordred_games || 0,
            morgana_wins: stats.morgana_wins || 0,
            morgana_games: stats.morgana_games || 0,
            assassin_wins: stats.assassin_wins || 0,
            assassin_games: stats.assassin_games || 0,
            assassin_merlin_kills: stats.assassin_merlin_kills || 0,
            minion_wins: stats.minion_wins || 0,
            minion_games: stats.minion_games || 0,
            oberon_wins: stats.oberon_wins || 0,
            oberon_games: stats.oberon_games || 0,
            win_rate: winRate,
            avg_score: avgScore,
          };
        });

        playerStats.sort((a, b) => (b.total_score || 0) - (a.total_score || 0));
        console.log('[V664] 底表实时计算完成，共', playerStats.length, '位玩家');

        // 应用扣分记录
        let penaltyQuery = supabase
          .from('penalty_records')
          .select('*')
          .eq('is_deleted', 'n');
        if (dojoFilter !== 'all') penaltyQuery = penaltyQuery.eq('dojo_id', Number(dojoFilter));
        if (seasonFilter !== 'all') penaltyQuery = penaltyQuery.eq('season_id', Number(seasonFilter));
        
        const { data: penaltyRecords, error: penaltyError } = await penaltyQuery;
        
        if (penaltyError) {
          console.error('[V664] 查询扣分记录失败:', penaltyError);
        } else if (penaltyRecords && penaltyRecords.length > 0) {
          console.log(`[V664] 查询到 ${penaltyRecords.length} 条扣分记录`);
          const penaltyMap = new Map<string, number>();
          for (const record of penaltyRecords) {
            const playerNickname = record.player_nickname?.trim();
            if (!playerNickname) continue;
            
            const score = Number(record.penalty_score) || 0;
            const currentPenalty = penaltyMap.get(playerNickname) || 0;
            penaltyMap.set(playerNickname, currentPenalty + score);
          }
          
          let totalPenaltyApplied = 0;
          for (const player of playerStats) {
            const penalty = penaltyMap.get(player.player_name) || 0;
            if (penalty !== 0) {
              player.total_score = (player.total_score || 0) + penalty;
              totalPenaltyApplied += penalty;
            }
          }
          console.log(`[V664] 共应用 ${totalPenaltyApplied} 分扣分`);
        }

        // 查询总场次和阵营胜率
        let countQuery = supabase
          .from('game_records')
          .select('*', { count: 'exact', head: true })
          .eq('is_deleted', 'n')
          .eq('status', 'valid');

        if (dojoFilter !== 'all') countQuery = countQuery.eq('dojo_id', Number(dojoFilter));
        if (seasonFilter !== 'all') countQuery = countQuery.eq('season_id', Number(seasonFilter));

        const { count: totalGames } = await countQuery;

        let goodQuery = supabase
          .from('game_records')
          .select('*', { count: 'exact', head: true })
          .eq('winning_faction', 'good')
          .eq('is_deleted', 'n')
          .eq('status', 'valid');

        let evilQuery = supabase
          .from('game_records')
          .select('*', { count: 'exact', head: true })
          .eq('winning_faction', 'evil')
          .eq('is_deleted', 'n')
          .eq('status', 'valid');

        if (dojoFilter !== 'all') {
          goodQuery = goodQuery.eq('dojo_id', Number(dojoFilter));
          evilQuery = evilQuery.eq('dojo_id', Number(dojoFilter));
        }
        if (seasonFilter !== 'all') {
          goodQuery = goodQuery.eq('season_id', Number(seasonFilter));
          evilQuery = evilQuery.eq('season_id', Number(seasonFilter));
        }

        const [{ count: goodWins }, { count: evilWins }] = await Promise.all([goodQuery, evilQuery]);

        const result = {
          playerStats,
          totalGames: totalGames || 0,
          factionWins: { good: goodWins || 0, evil: evilWins || 0 },
        };

        // V664-FIX: 强制刷新时也要更新缓存，确保后续访问有数据
        const cacheKey = `player_stats:${dojoFilter}:${seasonFilter}`;
        await statsCache.set(cacheKey, result);
        console.log('[V664] 已更新缓存');

        return result;
      }

      // V607: 非强制刷新时从 player_stats 缓存表查询（触发器自动维护）
      let query = supabase
        .from('player_stats')
        .select('*')
        .eq('is_deleted', 'n');

      if (dojoFilter !== 'all') {
        query = query.eq('dojo_id', Number(dojoFilter));
      }
      if (seasonFilter !== 'all') {
        query = query.eq('season_id', Number(seasonFilter));
      }

      const { data: playerStatsData, error: statsError } = await query;

      if (statsError) {
        console.error('[V607] 查询 player_stats 失败:', statsError);
        throw statsError;
      }

      console.log(`[V607] 查询到 ${playerStatsData?.length || 0} 条玩家统计数据`);

      // V607: 转换为前端格式
      const playerStats: LeaderboardPlayer[] = (playerStatsData || []).map(stats => {
        const winRate = calculateWinRate(stats.wins || 0, stats.total_games || 0);
        const avgScore = calculateAvgScore(stats.total_score || 0, stats.total_games || 0);
        return {
          player_number: stats.player_number || 0,
          player_name: stats.player_name || '',
          total_score: stats.total_score || 0,
          total_games: stats.total_games || 0,
          wins: stats.wins || 0,
          losses: stats.losses || 0,
          good_wins: stats.good_wins || 0,
          good_losses: stats.good_losses || 0,
          evil_wins: stats.evil_wins || 0,
          evil_losses: stats.evil_losses || 0,
          mvp_count: stats.mvp_count || 0,
          percival_wins: stats.percival_wins || 0,
          percival_games: stats.percival_games || 0,
          merlin_wins: stats.merlin_wins || 0,
          merlin_games: stats.merlin_games || 0,
          lancelot_good_wins: stats.lancelot_good_wins || 0,
          lancelot_good_games: stats.lancelot_good_games || 0,
          lancelot_evil_wins: stats.lancelot_evil_wins || 0,
          lancelot_evil_games: stats.lancelot_evil_games || 0,
          loyal_wins: stats.loyal_wins || 0,
          loyal_games: stats.loyal_games || 0,
          mordred_wins: stats.mordred_wins || 0,
          mordred_games: stats.mordred_games || 0,
          morgana_wins: stats.morgana_wins || 0,
          morgana_games: stats.morgana_games || 0,
          assassin_wins: stats.assassin_wins || 0,
          assassin_games: stats.assassin_games || 0,
          assassin_merlin_kills: stats.assassin_merlin_kills || 0,
          minion_wins: stats.minion_wins || 0,
          minion_games: stats.minion_games || 0,
          oberon_wins: stats.oberon_wins || 0,
          oberon_games: stats.oberon_games || 0,
          win_rate: winRate,
          avg_score: avgScore,
        };
      });

      playerStats.sort((a, b) => (b.total_score || 0) - (a.total_score || 0));

      console.log('[V607] 排序后玩家统计:', playerStats.slice(0, 5).map(p => `${p.player_name}: ${p.total_games}局 ${p.win_rate}%胜率`));

      // V607: 应用扣分记录（与原有逻辑相同）
      let penaltyQuery = supabase
        .from('penalty_records')
        .select('*')
        .eq('is_deleted', 'n');
      if (dojoFilter !== 'all') penaltyQuery = penaltyQuery.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') penaltyQuery = penaltyQuery.eq('season_id', Number(seasonFilter));
      
      const { data: penaltyRecords, error: penaltyError } = await penaltyQuery;
      
      if (penaltyError) {
        console.error('[V255] 查询扣分记录失败:', penaltyError);
      } else if (penaltyRecords && penaltyRecords.length > 0) {
        console.log(`[V255] 查询到 ${penaltyRecords.length} 条扣分记录`);
        const penaltyMap = new Map<string, number>();
        for (const record of penaltyRecords) {
          const playerNickname = record.player_nickname?.trim();
          if (!playerNickname) continue;
          
          const score = Number(record.penalty_score) || 0;
          const currentPenalty = penaltyMap.get(playerNickname) || 0;
          penaltyMap.set(playerNickname, currentPenalty + score);
        }
        
        let totalPenaltyApplied = 0;
        for (const player of playerStats) {
          const penalty = penaltyMap.get(player.player_name) || 0;
          if (penalty !== 0) {
            player.total_score = (player.total_score || 0) + penalty;
            totalPenaltyApplied += penalty;
          }
        }
        console.log(`[V255] 共应用 ${totalPenaltyApplied} 分扣分`);
      }

      // V607: 查询总场次和阵营胜率
      let countQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== 'all') countQuery = countQuery.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') countQuery = countQuery.eq('season_id', Number(seasonFilter));

      const { count: totalGames } = await countQuery;

      let goodQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('winning_faction', 'good')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      let evilQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('winning_faction', 'evil')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== 'all') {
        goodQuery = goodQuery.eq('dojo_id', Number(dojoFilter));
        evilQuery = evilQuery.eq('dojo_id', Number(dojoFilter));
      }
      if (seasonFilter !== 'all') {
        goodQuery = goodQuery.eq('season_id', Number(seasonFilter));
        evilQuery = evilQuery.eq('season_id', Number(seasonFilter));
      }

      const [{ count: goodWins }, { count: evilWins }] = await Promise.all([goodQuery, evilQuery]);

      const result = {
        playerStats,
        totalGames: totalGames || 0,
        factionWins: { good: goodWins || 0, evil: evilWins || 0 },
      };

      // V525: 写入本地缓存
      await statsCache.set(cacheKey, result);

      console.log('[V607] 玩家统计加载完成');
      return result;
    } catch (err) {
      console.error('[V607] 加载玩家统计失败:', err);
      throw err;
    }
  }

  async loadHistoryGames(
    dojoFilter: string, 
    seasonFilter: string,
    page: number = 1,
    pageSize: number = 100
  ): Promise<{ data: GameHistoryItem[]; total: number; hasMore: boolean }> {
    try {
      console.log(`[V610] 加载历史记录，第${page}页，每页${pageSize}条`);

      const offset = (page - 1) * pageSize;

      let query = supabase
        .from('game_records')
        .select('*', { count: 'exact' })
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));

      const { data: games, count, error } = await query;
      if (error) throw error;

      console.log(`[V610] 查询到游戏数：${games?.length}, 总记录数：${count}`);

      const gamesWithPlayers = await Promise.all(
        (games || []).map(async (game) => {
          const { data: players, error: playersError } = await supabase
            .from('player_round_records')
            .select('*')
            .eq('game_record_id', game.id)
            .eq('is_deleted', 'n')
            .eq('status', 'valid')
            .order('player_number', { ascending: true });

          if (playersError) {
            console.error('[V610] 查询玩家记录失败:', playersError, '游戏 ID:', game.id);
          }

          if (!players || players.length === 0) {
            console.warn('[V610] 游戏无玩家记录:', { gameId: game.id, gameDate: game.game_date });
          }

          const mvpPlayers = (players || []).filter(p => p.is_mvp);

          const taskResults = game.task_results as (boolean | null)[] || [];
          const missionResult = taskResults.filter(r => r !== null);
          const successCount = missionResult.filter(r => r === true).length;
          const failCount = missionResult.filter(r => r === false).length;

          let assassinAttempt = '';
          if (game.victory_type === 'assassin_success') {
            assassinAttempt = '刺杀梅林成功';
          } else if (game.victory_type === 'assassin_fail') {
            assassinAttempt = '提前刺杀失败';
          }

          return {
            ...game,
            players: players || [],
            mvp_names: mvpPlayers.map(p => p.player_nickname || `${p.player_number}号`),
            mission_summary: `${successCount}胜${failCount}败`,
            assassin_attempt: assassinAttempt,
          };
        })
      );

      const gamesWithPlayersCount = gamesWithPlayers.filter(g => g.players && g.players.length > 0).length;
      console.log(`[V610] 有玩家数据的游戏数：${gamesWithPlayersCount} / ${gamesWithPlayers.length}`);

      const total = count || 0;
      const hasMore = offset + gamesWithPlayers.length < total;

      return { data: gamesWithPlayers, total, hasMore };
    } catch (err) {
      console.error('[V610] 加载历史记录失败:', err);
      throw err;
    }
  }

  async invalidateGameRecords(gameId: number): Promise<void> {
    try {
      console.log(`[V109] 标记游戏 ${gameId} 的旧记录为无效`);

      const { error: gameError } = await supabase
        .from('game_records')
        .update({ status: 'invalid' })
        .eq('id', gameId);

      if (gameError) throw gameError;

      const { error: playerError } = await supabase
        .from('player_round_records')
        .update({ status: 'invalid' })
        .eq('game_record_id', gameId);

      if (playerError) throw playerError;

      console.log(`[V109] 游戏 ${gameId} 的旧记录已标记为无效`);
    } catch (err) {
      console.error('[V109] 标记旧记录失败:', err);
      throw err;
    }
  }

  async fetchPlayerDebugDetails(playerNumber: number): Promise<DebugPlayerRecord[]> {
    try {
      const { data: records, error } = await supabase
        .from('player_round_records')
        .select('*, game_records!inner(*)')
        .eq('player_number', playerNumber)
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (error) throw error;

      return (records || []).map((record: any) => ({
        record_id: record.id,
        game_id: record.game_record_id,
        game_date: record.game_records?.game_date,
        game_number: record.game_records?.game_number,
        player_nickname: record.player_nickname,
        player_role: record.player_role,
        faction: record.faction,
        score_change: record.score_change,
        is_mvp: record.is_mvp,
        is_win: Number(record.score_change) > 0,
        victory_type: record.game_records?.victory_type,
        winning_faction: record.game_records?.winning_faction,
        created_at: record.created_at,
        status: record.status,
      }));
    } catch (err) {
      console.error('[V109] 获取调试数据失败:', err);
      return [];
    }
  }

  async calculateRoleLeaderboard(role: 'percival' | 'merlin' | 'assassin'): Promise<any[]> {
    try {
      const { data: games } = await supabase
        .from('game_records')
        .select('*, player_round_records(*)')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (!games) return [];

      const playerStats: Record<number, {
        player_number: number;
        player_name: string;
        wins: number;
        games: number;
        merlinKills?: number;
      }> = {};

      for (const game of games) {
        const players = game.player_round_records || [];
        for (const player of players) {
          if (player.is_deleted === 'y' || player.status !== 'valid') continue;
          if (player.player_role !== role) continue;

          const isWin = game.winning_faction === player.faction;
          const playerNum = player.player_number;

          if (!playerStats[playerNum]) {
            playerStats[playerNum] = {
              player_number: playerNum,
              player_name: player.player_nickname || `${playerNum}号`,
              wins: 0,
              games: 0,
              merlinKills: 0,
            };
          }

          playerStats[playerNum].games++;

          if (role === 'percival' || role === 'merlin') {
            if (player.faction === 'good' && isWin) {
              playerStats[playerNum].wins++;
            }
          }

          if (role === 'assassin') {
            if (isWin && game.victory_type === 'assassin_success') {
              playerStats[playerNum].wins++;
              playerStats[playerNum].merlinKills!++;
            }
          }
        }
      }

      return Object.values(playerStats).sort((a, b) => b.wins - a.wins);
    } catch (err) {
      console.error('[V109] 计算角色排行失败:', err);
      return [];
    }
  }

  /**
   * V251: 保存独立扣分记录
   */
  async savePenaltyRecord(record: Omit<PenaltyRecord, 'id' | 'created_at' | 'updated_at'>): Promise<{ success: boolean; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('penalty_records')
        .insert({
          player_nickname: record.player_nickname,
          penalty_type: record.penalty_type,
          penalty_score: record.penalty_score,
          reason: record.reason || null,
          penalty_date: record.penalty_date || new Date().toISOString().split('T')[0],
          dojo_id: record.dojo_id || null,
          season_id: record.season_id || null,
          player_count: record.player_count || 3,
          emp_id: record.emp_id,
          corp_id: record.corp_id || null,
        })
        .select()
        .single();

      if (error) {
        console.error('[V251] 保存扣分记录失败:', error);
        return { success: false, error: error.message };
      }

      // V619: 清除玩家统计缓存（扣分影响总积分）
      statsCache.remove(`player_stats:${record.dojo_id || 'all'}:${record.season_id || 'all'}`);
      statsCache.remove('player_stats:all:all');
      // 清除扣分记录缓存
      statsCache.remove(`penalty_records:${record.dojo_id || 'all'}:${record.season_id || 'all'}`);
      console.log('[V619] 已清除扣分相关缓存');

      console.log('[V251] 扣分记录保存成功:', data);
      return { success: true };
    } catch (err) {
      console.error('[V251] 保存扣分记录异常:', err);
      return { success: false, error: '保存失败，请重试' };
    }
  }

  /**
   * V251: 获取玩家的所有扣分记录
   */
  async getPlayerPenaltyRecords(playerNickname: string, dojoFilter: string = 'all', seasonFilter: string = 'all'): Promise<PenaltyRecord[]> {
    try {
      let query = supabase
        .from('penalty_records')
        .select('*')
        .eq('player_nickname', playerNickname)
        .eq('is_deleted', 'n')
        .order('created_at', { ascending: false });

      if (dojoFilter !== 'all') {
        query = query.eq('dojo_id', parseInt(dojoFilter));
      }

      if (seasonFilter !== 'all') {
        query = query.eq('season_id', parseInt(seasonFilter));
      }

      const { data, error } = await query;

      if (error) {
        console.error('[V251] 获取扣分记录失败:', error);
        return [];
      }

      return data || [];
    } catch (err) {
      console.error('[V251] 获取扣分记录异常:', err);
      return [];
    }
  }

  /**
   * V251: 获取指定道场/赛季的所有扣分记录
   */
  async getAllPenaltyRecords(dojoFilter?: number, seasonFilter?: number): Promise<PenaltyRecord[]> {
    try {
      // V525: 检查本地缓存
      const cacheKey = `penalty_records:${dojoFilter || 'all'}:${seasonFilter || 'all'}`;
      const cached = await statsCache.get<PenaltyRecord[]>(cacheKey);
      if (cached) {
        console.log('[V525] 扣分记录使用缓存数据');
        return cached;
      }
      console.log('[V525] 扣分记录缓存未命中，从数据库加载');

      let query = supabase
        .from('penalty_records')
        .select('*')
        .eq('is_deleted', 'n')
        .order('created_at', { ascending: false });

      if (dojoFilter !== undefined) {
        query = query.eq('dojo_id', dojoFilter);
      }

      if (seasonFilter !== undefined) {
        query = query.eq('season_id', seasonFilter);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[V251] 获取所有扣分记录失败:', error);
        return [];
      }

      // V525: 保存到本地缓存
      const dateStr = data?.[0]?.penalty_date || data?.[0]?.created_at?.split('T')[0];
      await statsCache.set(cacheKey, data || [], dateStr);

      return data || [];
    } catch (err) {
      console.error('[V251] 获取所有扣分记录异常:', err);
      return [];
    }
  }

  /**
   * V251: 删除扣分记录（软删除）
   */
  async deletePenaltyRecord(id: number): Promise<{ success: boolean; error?: string }> {
    try {
      // V619: 先查询原始记录，获取 dojo_id 和 season_id 用于清除缓存
      const { data: originalRecord } = await supabase
        .from('penalty_records')
        .select('dojo_id, season_id')
        .eq('id', id)
        .single();

      const { error } = await supabase
        .from('penalty_records')
        .update({ is_deleted: 'y', updated_at: new Date().toISOString() })
        .eq('id', id);

      if (error) {
        console.error('[V251] 删除扣分记录失败:', error);
        return { success: false, error: error.message };
      }

      // V619: 清除相关缓存
      if (originalRecord) {
        statsCache.remove(`player_stats:${originalRecord.dojo_id || 'all'}:${originalRecord.season_id || 'all'}`);
        statsCache.remove('player_stats:all:all');
        statsCache.remove(`penalty_records:${originalRecord.dojo_id || 'all'}:${originalRecord.season_id || 'all'}`);
        console.log('[V619] 已清除扣分相关缓存');
      }

      console.log('[V251] 扣分记录删除成功:', id);
      return { success: true };
    } catch (err) {
      console.error('[V251] 删除扣分记录异常:', err);
      return { success: false, error: '删除失败，请重试' };
    }
  }

  async updatePenaltyRecord(id: number, record: Partial<PenaltyRecord>): Promise<{ success: boolean; error?: string }> {
    try {
      // V619: 先查询原始记录，获取 dojo_id 和 season_id 用于清除缓存
      const { data: originalRecord } = await supabase
        .from('penalty_records')
        .select('dojo_id, season_id')
        .eq('id', id)
        .single();

      const updateData: any = {
        updated_at: new Date().toISOString(),
      };

      if (record.player_nickname !== undefined) updateData.player_nickname = record.player_nickname;
      if (record.penalty_type !== undefined) updateData.penalty_type = record.penalty_type;
      if (record.penalty_score !== undefined) updateData.penalty_score = record.penalty_score;
      if (record.reason !== undefined) updateData.reason = record.reason;
      if (record.penalty_date !== undefined) updateData.penalty_date = record.penalty_date;
      if (record.dojo_id !== undefined) updateData.dojo_id = record.dojo_id;
      if (record.season_id !== undefined) updateData.season_id = record.season_id;
      if (record.player_count !== undefined) updateData.player_count = record.player_count;

      const { error } = await supabase
        .from('penalty_records')
        .update(updateData)
        .eq('id', id);

      if (error) {
        console.error('[V253] 更新扣分记录失败:', error);
        return { success: false, error: error.message };
      }

      // V619: 清除相关缓存（使用新旧 dojo_id/season_id 中最大的范围）
      const oldDojoId = originalRecord?.dojo_id || record.dojo_id;
      const oldSeasonId = originalRecord?.season_id || record.season_id;
      statsCache.remove(`player_stats:${oldDojoId || 'all'}:${oldSeasonId || 'all'}`);
      statsCache.remove('player_stats:all:all');
      statsCache.remove(`penalty_records:${oldDojoId || 'all'}:${oldSeasonId || 'all'}`);
      console.log('[V619] 已清除扣分相关缓存');

      console.log('[V253] 扣分记录更新成功:', id);
      return { success: true };
    } catch (err) {
      console.error('[V253] 更新扣分记录异常:', err);
      return { success: false, error: '更新失败，请重试' };
    }
  }

  /**
   * V259: 带时间范围加载玩家统计（V527: 支持智能数据源选择）
   */
  async loadPlayerStatsWithDateRange(
    dojoFilter?: number,
    seasonFilter?: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<{
    playerStats: LeaderboardPlayer[];
    totalGames: number;
    factionWins: { good: number; evil: number };
  }> {
    try {
      const startDateStr = startDate?.toISOString().split('T')[0];
      const endDateStr = endDate?.toISOString().split('T')[0];
      
      // V527: 使用智能数据源选择
      let records: any[] = [];
      let useMaterializedView = false;
      
      if (startDateStr && endDateStr) {
        const result = await this.fetchWithSmartDataSource(
          startDateStr,
          endDateStr,
          dojoFilter,
          seasonFilter
        );
        useMaterializedView = result.useMaterializedView;
        records = result.data;
      } else {
        // 没有时间范围，使用原始表
        records = await this.fetchRawPlayerRecords(
          dojoFilter !== undefined ? String(dojoFilter) : 'all',
          seasonFilter !== undefined ? String(seasonFilter) : 'all'
        );
      }

      // V527: 根据数据源处理结果
      if (useMaterializedView) {
        // 物化视图返回的是聚合数据，直接转换格式
        console.log('[V527] 处理物化视图数据');
        
        // 注意：物化视图不包含详细的玩家统计数据，只返回每日汇总
        // 这里返回空数组，上层 UI 需要特殊处理
        return {
          playerStats: [],
          totalGames: records.reduce((sum, r) => sum + (r.game_count || 0), 0),
          factionWins: {
            good: records.reduce((sum, r) => sum + (r.good_wins || 0), 0),
            evil: records.reduce((sum, r) => sum + (r.evil_wins || 0), 0),
          },
        };
      }
      
      // 使用原始表的逻辑（原有代码）
      const statsMap = this.aggregatePlayerStats(records);

      const playerStats: LeaderboardPlayer[] = Object.values(statsMap).map(stats => {
        const winRate = calculateWinRate(stats.wins || 0, stats.total_games || 0);
        const avgScore = calculateAvgScore(stats.total_score || 0, stats.total_games || 0);
        return {
          player_number: stats.player_number || 0,
          player_name: stats.player_name || '',
          total_score: stats.total_score || 0,
          total_games: stats.total_games || 0,
          wins: stats.wins || 0,
          losses: stats.losses || 0,
          good_wins: stats.good_wins || 0,
          good_losses: stats.good_losses || 0,
          evil_wins: stats.evil_wins || 0,
          evil_losses: stats.evil_losses || 0,
          mvp_count: stats.mvp_count || 0,
          percival_wins: stats.percival_wins || 0,
          percival_games: stats.percival_games || 0,
          merlin_wins: stats.merlin_wins || 0,
          merlin_games: stats.merlin_games || 0,
          lancelot_good_wins: stats.lancelot_good_wins || 0,
          lancelot_good_games: stats.lancelot_good_games || 0,
          lancelot_evil_wins: stats.lancelot_evil_wins || 0,
          lancelot_evil_games: stats.lancelot_evil_games || 0,
          loyal_wins: stats.loyal_wins || 0,
          loyal_games: stats.loyal_games || 0,
          mordred_wins: stats.mordred_wins || 0,
          mordred_games: stats.mordred_games || 0,
          morgana_wins: stats.morgana_wins || 0,
          morgana_games: stats.morgana_games || 0,
          assassin_wins: stats.assassin_wins || 0,
          assassin_games: stats.assassin_games || 0,
          assassin_merlin_kills: stats.assassin_merlin_kills || 0,
          minion_wins: stats.minion_wins || 0,
          minion_games: stats.minion_games || 0,
          oberon_wins: stats.oberon_wins || 0,
          oberon_games: stats.oberon_games || 0,
          win_rate: winRate,
          avg_score: avgScore,
        };
      });

      playerStats.sort((a, b) => (b.total_score || 0) - (a.total_score || 0));

      let penaltyQuery = supabase
        .from('penalty_records')
        .select('*')
        .eq('is_deleted', 'n');
      if (dojoFilter !== undefined) penaltyQuery = penaltyQuery.eq('dojo_id', dojoFilter);
      if (seasonFilter !== undefined) penaltyQuery = penaltyQuery.eq('season_id', seasonFilter);
      if (startDate && endDate) {
        penaltyQuery = penaltyQuery.gte('penalty_date', startDate.toISOString().split('T')[0])
          .lte('penalty_date', endDate.toISOString().split('T')[0]);
      }
      
      const { data: penaltyRecords, error: penaltyError } = await penaltyQuery;
      
      if (penaltyError) {
        console.error('[V259] 查询扣分记录失败:', penaltyError);
      } else if (penaltyRecords && penaltyRecords.length > 0) {
        const penaltyMap = new Map<string, number>();
        for (const record of penaltyRecords) {
          const playerNickname = record.player_nickname?.trim();
          if (!playerNickname) continue;
          
          const score = Number(record.penalty_score) || 0;
          const currentPenalty = penaltyMap.get(playerNickname) || 0;
          penaltyMap.set(playerNickname, currentPenalty + score);
        }
        
        for (const player of playerStats) {
          const penalty = penaltyMap.get(player.player_name) || 0;
          if (penalty !== 0) {
            player.total_score = (player.total_score || 0) + penalty;
          }
        }
      }

      let countQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== undefined) countQuery = countQuery.eq('dojo_id', dojoFilter);
      if (seasonFilter !== undefined) countQuery = countQuery.eq('season_id', seasonFilter);
      if (startDate && endDate) {
        countQuery = countQuery.gte('game_date', startDate.toISOString().split('T')[0])
          .lte('game_date', endDate.toISOString().split('T')[0]);
      }

      const { count: totalGames } = await countQuery;

      let goodQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('winning_faction', 'good')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      let evilQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('winning_faction', 'evil')
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== undefined) {
        goodQuery = goodQuery.eq('dojo_id', dojoFilter);
        evilQuery = evilQuery.eq('dojo_id', dojoFilter);
      }
      if (seasonFilter !== undefined) {
        goodQuery = goodQuery.eq('season_id', seasonFilter);
        evilQuery = evilQuery.eq('season_id', seasonFilter);
      }
      if (startDate && endDate) {
        goodQuery = goodQuery.gte('game_date', startDate.toISOString().split('T')[0])
          .lte('game_date', endDate.toISOString().split('T')[0]);
        evilQuery = evilQuery.gte('game_date', startDate.toISOString().split('T')[0])
          .lte('game_date', endDate.toISOString().split('T')[0]);
      }

      const { count: goodCount } = await goodQuery;
      const { count: evilCount } = await evilQuery;

      return { 
        playerStats, 
        totalGames: totalGames || 0, 
        factionWins: { good: goodCount || 0, evil: evilCount || 0 } 
      };
    } catch (err) {
      console.error('[V259] 加载玩家统计失败:', err);
      throw err;
    }
  }

  /**
   * V259: 带时间范围加载历史记录
   */
  async loadHistoryGamesWithDateRange(
    dojoFilter?: number,
    seasonFilter?: number,
    startDate?: Date,
    endDate?: Date
  ): Promise<GameRecord[]> {
    try {
      let query = supabase
        .from('game_records')
        .select('*')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false });

      if (dojoFilter !== undefined) {
        query = query.eq('dojo_id', dojoFilter);
      }
      if (seasonFilter !== undefined) {
        query = query.eq('season_id', seasonFilter);
      }
      if (startDate && endDate) {
        // V547-FIX: 使用日期字符串而不是 ISO 时间戳，避免时区转换问题
        const startDateStr = startDate.toISOString().split('T')[0];
        const endDateStr = endDate.toISOString().split('T')[0];
        console.log(`[V547] 时间范围筛选（V259）：${startDateStr} 至 ${endDateStr}`);
        
        query = query.gte('game_date', startDateStr)
          .lte('game_date', endDateStr);
      }

      const { data, error } = await query;

      if (error) {
        console.error('[V259] 查询历史记录失败:', error);
        return [];
      }

      console.log(`[V259] 查询到 ${data?.length || 0} 条历史记录`);
      return data || [];
    } catch (err) {
      console.error('[V259] 查询历史记录异常:', err);
      return [];
    }
  }

  /**
   * V550: 诊断方法 - 查询数据库中所有有效游戏记录的日期范围
   */
  async diagnoseGameDateRange(): Promise<{ minDate: string | null; maxDate: string | null; totalCount: number }> {
    try {
      // 查询最早的游戏日期
      const { data: minData, error: minError } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: true })
        .limit(1);

      // 查询最晚的游戏日期
      const { data: maxData, error: maxError } = await supabase
        .from('game_records')
        .select('game_date')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .limit(1);

      // 查询总记录数
      const { count: totalCount, error: countError } = await supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (minError || maxError || countError) {
        console.error('[V550] 诊断查询失败:', { minError, maxError, countError });
        return { minDate: null, maxDate: null, totalCount: 0 };
      }

      const minDate = minData && minData.length > 0 ? minData[0].game_date : null;
      const maxDate = maxData && maxData.length > 0 ? maxData[0].game_date : null;

      console.log(`[V550-DIAGNOSE] 数据库游戏记录日期范围：${minDate} 至 ${maxDate}, 总数：${totalCount}`);

      return { minDate, maxDate, totalCount: totalCount || 0 };
    } catch (err) {
      console.error('[V550] 诊断查询异常:', err);
      return { minDate: null, maxDate: null, totalCount: 0 };
    }
  }

  /**
   * V546: 加载历史记录基本信息（不包含玩家详情，用于快速展示列表）
   */
  async loadHistoryGamesBasic(
    dojoFilter: string, 
    seasonFilter: string,
    timeRange?: { start: Date; end: Date }
  ): Promise<GameHistoryItem[]> {
    try {
      let query = supabase
        .from('game_records')
        .select('*')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false });

      if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));
      
      if (timeRange) {
        // V550-FIX: 使用日期字符串而不是 ISO 时间戳，避免时区转换问题
        const startDateStr = timeRange.start.toISOString().split('T')[0];
        const endDateStr = timeRange.end.toISOString().split('T')[0];
        
        console.log(`[V550] 加载历史记录基本信息 - 时间范围筛选：${startDateStr} 至 ${endDateStr}`);
        console.log(`[V550] 原始时间范围对象：start=${timeRange.start}, end=${timeRange.end}`);
        
        query = query
          .gte('game_date', startDateStr)
          .lte('game_date', endDateStr);
      } else {
        console.log(`[V550] 加载历史记录基本信息 - 无时间范围筛选`);
      }

      const { data: games, error } = await query;
      if (error) throw error;

      console.log(`[V550] 加载历史记录基本信息：${games?.length || 0}条记录`);
      
      // V550-DEBUG: 输出最早和最晚的游戏日期
      if (games && games.length > 0) {
        const dates = games.map(g => g.game_date).filter(Boolean).sort();
        console.log(`[V550] 游戏日期范围：${dates[0]} 至 ${dates[dates.length - 1]}`);
      }

      // 返回基本信息，players 字段为空数组，等待懒加载
      return (games || []).map(game => {
        const taskResults = game.task_results as (boolean | null)[] || [];
        const missionResult = taskResults.filter(r => r !== null);
        const successCount = missionResult.filter(r => r === true).length;
        const failCount = missionResult.filter(r => r === false).length;

        let assassinAttempt = '';
        if (game.victory_type === 'assassin_success') {
          assassinAttempt = '刺杀梅林成功';
        } else if (game.victory_type === 'assassin_fail') {
          assassinAttempt = '提前刺杀失败';
        }

        return {
          ...game,
          players: [],
          mvp_names: [],
          mission_summary: `${successCount}胜${failCount}败`,
          assassin_attempt: assassinAttempt,
        };
      });
    } catch (err) {
      console.error('[V550] 加载历史记录基本信息失败:', err);
      throw err;
    }
  }

  /**
   * V546: 懒加载单个游戏的玩家数据
   */
  async loadGamePlayers(gameId: number): Promise<any[]> {
    try {
      const { data: players, error } = await supabase
        .from('player_round_records')
        .select('*')
        .eq('game_record_id', gameId)
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('player_number', { ascending: true });

      if (error) throw error;

      return players || [];
    } catch (err) {
      console.error(`[V546] 加载游戏 ${gameId} 的玩家数据失败:`, err);
      throw err;
    }
  }

  /**
   * V526: 分页加载历史记录（每页 20 条）
   * @param page 页码（从 1 开始）
   * @param pageSize 每页数量（默认 20）
   */
  async loadAllHistoryGames(
    dojoFilter: string, 
    seasonFilter: string,
    timeRange?: { start: Date; end: Date }
  ): Promise<{ games: GameHistoryItem[]; total: number }> {
    try {
      // 先获取总数
      let countQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== 'all') countQuery = countQuery.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') countQuery = countQuery.eq('season_id', Number(seasonFilter));
      
      if (timeRange) {
        // V547-FIX: 使用日期字符串而不是 ISO 时间戳，避免时区转换问题
        const startDateStr = timeRange.start.toISOString().split('T')[0];
        const endDateStr = timeRange.end.toISOString().split('T')[0];
        
        countQuery = countQuery
          .gte('game_date', startDateStr)
          .lte('game_date', endDateStr);
      }

      const { count: total } = await countQuery;

      // 查询所有游戏记录（不分页）
      let query = supabase
        .from('game_records')
        .select('*')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false });

      if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));
      
      if (timeRange) {
        // V547-FIX: 使用日期字符串而不是 ISO 时间戳，避免时区转换问题
        const startDateStr = timeRange.start.toISOString().split('T')[0];
        const endDateStr = timeRange.end.toISOString().split('T')[0];
        
        query = query
          .gte('game_date', startDateStr)
          .lte('game_date', endDateStr);
      }

      const { data: games, error } = await query;
      if (error) throw error;

      console.log(`[V545] 加载全部历史记录：${games?.length || 0}条记录`);

      // V540: 批量加载玩家记录
      const gameIds = (games || []).map(g => g.id);
      let allPlayers: any[] = [];
      
      if (gameIds.length > 0) {
        const { data: playersData, error: playersError } = await supabase
          .from('player_round_records')
          .select('*')
          .in('game_record_id', gameIds)
          .eq('is_deleted', 'n')
          .eq('status', 'valid')
          .order('game_record_id', { ascending: true })
          .order('player_number', { ascending: true });

        if (playersError) {
          console.error('[V540] 批量查询玩家记录失败:', playersError);
        } else {
          allPlayers = playersData || [];
        }
      }

      // 按 game_record_id 分组玩家数据
      const playersByGameId = new Map<number, any[]>();
      allPlayers.forEach(player => {
        const gameId = player.game_record_id;
        if (!playersByGameId.has(gameId)) {
          playersByGameId.set(gameId, []);
        }
        playersByGameId.get(gameId)!.push(player);
      });

      // 处理每个游戏记录
      const gamesWithPlayers = (games || []).map(game => {
        const players = playersByGameId.get(game.id) || [];
        const mvpPlayers = players.filter(p => p.is_mvp);

        const taskResults = game.task_results as (boolean | null)[] || [];
        const missionResult = taskResults.filter(r => r !== null);
        const successCount = missionResult.filter(r => r === true).length;
        const failCount = missionResult.filter(r => r === false).length;

        let assassinAttempt = '';
        if (game.victory_type === 'assassin_success') {
          assassinAttempt = '刺杀梅林成功';
        } else if (game.victory_type === 'assassin_fail') {
          assassinAttempt = '提前刺杀失败';
        }

        return {
          ...game,
          players: players,
          mvp_names: mvpPlayers.map(p => p.player_nickname || `${p.player_number}号`),
          mission_summary: `${successCount}胜${failCount}败`,
          assassin_attempt: assassinAttempt,
        };
      });

      return { games: gamesWithPlayers, total: total || 0 };
    } catch (err) {
      console.error('[V545] 加载全部历史记录失败:', err);
      throw err;
    }
  }

  async loadHistoryGamesPaginated(
    dojoFilter: string, 
    seasonFilter: string,
    timeRange?: { start: Date; end: Date },
    page: number = 1,
    pageSize: number = 20
  ): Promise<{ games: GameHistoryItem[]; hasMore: boolean; total: number }> {
    try {
      const offset = (page - 1) * pageSize;
      
      // 先获取总数
      let countQuery = supabase
        .from('game_records')
        .select('*', { count: 'exact', head: true })
        .eq('is_deleted', 'n')
        .eq('status', 'valid');

      if (dojoFilter !== 'all') countQuery = countQuery.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') countQuery = countQuery.eq('season_id', Number(seasonFilter));
      
      // V544-P0: 添加时间范围筛选（数据库层）
      if (timeRange) {
        const startDateStr = timeRange.start.toISOString().split('T')[0];
        const endDateStr = timeRange.end.toISOString().split('T')[0];
        console.log(`[V550] 分页加载时间范围：${startDateStr} 至 ${endDateStr}`);
        countQuery = countQuery
          .gte('game_date', startDateStr)
          .lte('game_date', endDateStr);
      }

      const { count: total } = await countQuery;

      // 分页查询游戏记录
      let query = supabase
        .from('game_records')
        .select('*')
        .eq('is_deleted', 'n')
        .eq('status', 'valid')
        .order('game_date', { ascending: false })
        .order('game_number', { ascending: false })
        .range(offset, offset + pageSize - 1);

      if (dojoFilter !== 'all') query = query.eq('dojo_id', Number(dojoFilter));
      if (seasonFilter !== 'all') query = query.eq('season_id', Number(seasonFilter));
      
      // V544-P0: 添加时间范围筛选（数据库层）
      if (timeRange) {
        const startDateStr = timeRange.start.toISOString().split('T')[0];
        const endDateStr = timeRange.end.toISOString().split('T')[0];
        query = query
          .gte('game_date', startDateStr)
          .lte('game_date', endDateStr);
      }

      const { data: games, error } = await query;
      if (error) throw error;

      console.log(`[V526] 分页加载历史记录：第${page}页，${games?.length || 0}条记录`);

      // V540: 批量加载玩家记录（优化：一次性查询当前页所有游戏的玩家数据，避免 N+1 查询）
      const gameIds = (games || []).map(g => g.id);
      let allPlayers: any[] = [];
      
      if (gameIds.length > 0) {
        const { data: playersData, error: playersError } = await supabase
          .from('player_round_records')
          .select('*')
          .in('game_record_id', gameIds)
          .eq('is_deleted', 'n')
          .eq('status', 'valid')
          .order('game_record_id', { ascending: true })
          .order('player_number', { ascending: true });

        if (playersError) {
          console.error('[V540] 批量查询玩家记录失败:', playersError);
        } else {
          allPlayers = playersData || [];
        }
      }

      // 按 game_record_id 分组玩家数据
      const playersByGameId = new Map<number, any[]>();
      allPlayers.forEach(player => {
        const gameId = player.game_record_id;
        if (!playersByGameId.has(gameId)) {
          playersByGameId.set(gameId, []);
        }
        playersByGameId.get(gameId)!.push(player);
      });

      // 处理每个游戏记录
      const gamesWithPlayers = (games || []).map(game => {
        const players = playersByGameId.get(game.id) || [];
        const mvpPlayers = players.filter(p => p.is_mvp);

        const taskResults = game.task_results as (boolean | null)[] || [];
        const missionResult = taskResults.filter(r => r !== null);
        const successCount = missionResult.filter(r => r === true).length;
        const failCount = missionResult.filter(r => r === false).length;

        let assassinAttempt = '';
        if (game.victory_type === 'assassin_success') {
          assassinAttempt = '刺杀梅林成功';
        } else if (game.victory_type === 'assassin_fail') {
          assassinAttempt = '提前刺杀失败';
        }

        return {
          ...game,
          players: players,
          mvp_names: mvpPlayers.map(p => p.player_nickname || `${p.player_number}号`),
          mission_summary: `${successCount}胜${failCount}败`,
          assassin_attempt: assassinAttempt,
        };
      });

      const hasMore = (games?.length || 0) === pageSize;

      return { games: gamesWithPlayers, hasMore, total: total || 0 };
    } catch (err) {
      console.error('[V526] 分页加载历史记录失败:', err);
      throw err;
    }
  }

  /**
   * V658: 获取服务端数据版本号（用于多浏览器同步）
   */
  async getDataVersion(): Promise<number> {
    try {
      const { data, error } = await supabase
        .from('data_version_cce0d86f_8f5')
        .select('version')
        .eq('id', 1)
        .single();

      if (error) {
        console.error('[V658] 查询数据版本失败:', error);
        return 0;
      }

      return data?.version || 0;
    } catch (err) {
      console.error('[V658] 获取数据版本异常:', err);
      return 0;
    }
  }
}

export const statsDataService = new StatsDataService();
